import { useContext, useState } from 'react'
import type { Lease, VmDisk } from '@/api/types'
import { leases as leaseApi } from '@/api/endpoints'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CopyButton } from '@/components/CopyButton'
import { Err } from '@/components/Err'
import { ExpertLine } from '@/components/Expert'
import { Section } from '@/components/layout/Page'
import { diskWord, mayChange } from '@/domain/lease'
import { SessionCtx } from '@/hooks/useSession'
import { cn } from '@/lib/utils'
import { size as sizeText } from '@/ui/format'
import { CHIP } from './vm/chips'

const SIZES = [16, 32, 64, 128, 256, 512, 1024]
// 되돌릴 수 없는 디스크 조작은 좁은 화면에서 감춘다. `cn` 으로 합쳐야 감추기가 이긴다 —
// 문자열로 이어 붙이면 `TOUCH` 의 `max-sm:inline-flex` 가 생성된 CSS 에서 `max-sm:hidden` 뒤에 와서 칩이 그대로 선다.
const DISK_CHIP = cn(CHIP, 'max-sm:hidden')
type Change = { action: 'resize' | 'remove'; disk: VmDisk }

// 데이터 디스크 붙이기 — `추가` 칩과 크기 대화 상자. 관리자 VM 상세의 디스크 절이 이 칩과 함께 목록·확장·삭제를 둔다.
// 조건: 사용 중·그 VM 을 바꿀 수 있는 사람(`mayChange` — 소유자와 최고 관리자)·lock 없음·진행 중 디스크 없음·16개 미만.
export function DiskAddChip({ lease, onChanged, onAccepted }: { lease: Lease; onChanged: () => void; onAccepted?: (result: Lease) => void }) {
  const user = useContext(SessionCtx)
  const [open, setOpen] = useState(false)
  const [size, setSize] = useState(64)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const disks = lease.vm?.disks ?? []
  const canEdit = lease.state === 'active' && mayChange(lease, user)
  const locked = busy || !canEdit || Boolean(lease.vm?.observed_lock) || disks.some((d) => d.state !== 'attached')
  const save = async () => {
    if (locked) return
    setBusy(true)
    setError(null)
    try {
      const result = await leaseApi.attachDisk(lease.id, size)
      onAccepted?.(result)
      setOpen(false)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  if (!lease.vm || !canEdit) return null
  return (
    <>
      <button type="button" className={DISK_CHIP} disabled={locked || disks.length >= 16} onClick={() => { setError(null); setSize(64); setOpen(true) }}>추가</button>
      <Dialog open={open} onOpenChange={(value) => { if (!value && !busy) setOpen(false) }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader><DialogTitle>디스크 추가</DialogTitle></DialogHeader>
          <select aria-label="디스크 크기" className="rounded-md border bg-background px-3 py-2" value={size} onChange={(event) => setSize(Number(event.target.value))} disabled={busy}>{SIZES.map((n) => <option key={n} value={n}>{sizeText(n)}</option>)}</select>
          <Err error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>취소</Button>
            <Button type="button" onClick={save} disabled={locked}>{busy ? '처리 중' : '추가'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// 관리자 VM 상세의 디스크 절: 목록과 확장·삭제, 전문가 행에 저장 위치(`admin`). 붙이기는 위의 칩이다.
export function DiskPanel({ lease, admin = false, onChanged }: { lease: Lease; admin?: boolean; onChanged: () => void }) {
  const user = useContext(SessionCtx)
  const [change, setChange] = useState<Change | null>(null)
  const [size, setSize] = useState(64)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [accepted, setAccepted] = useState<{ source: Lease; value: Lease } | null>(null)
  const current = accepted?.source === lease ? accepted.value : lease
  const disks = current.vm?.disks ?? []
  const canEdit = lease.state === 'active' && mayChange(lease, user)
  const locked = busy || !canEdit || Boolean(lease.vm?.observed_lock) || disks.some((d) => d.state !== 'attached')
  const title = change?.action === 'resize' ? `디스크 ${change.disk.slot} 확장` : `디스크 ${change?.disk.slot} 삭제`
  const sizes = change?.action === 'resize' ? SIZES.filter((n) => n > change.disk.size_gb) : SIZES
  const open = (next: Change) => {
    setError(null)
    setSize(next.action === 'resize' ? SIZES.find((n) => n > next.disk.size_gb) ?? 1024 : 64)
    setChange(next)
  }
  const save = async () => {
    if (!change || locked) return
    setBusy(true)
    setError(null)
    try {
      const result =
        change.action === 'resize' ? await leaseApi.resizeDisk(lease.id, change.disk.slot, size) : await leaseApi.removeDisk(lease.id, change.disk.slot)
      setAccepted({ source: lease, value: result })
      setChange(null)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  if (!lease.vm) return null
  return (
    <Section title="디스크" right={<DiskAddChip lease={current} onChanged={onChanged} onAccepted={(value) => setAccepted({ source: lease, value })} />}>
      <div className="divide-y rounded-lg border bg-card px-4">
        {disks.map((disk) => (
          <div key={disk.slot} className="py-3">
            <div className="flex items-center gap-2 text-[12px]">
              <span className="min-w-0 flex-1">{disk.is_boot ? '루트' : `디스크 ${disk.slot}`} · {sizeText(disk.size_gb)}{disk.is_boot ? '' : ` · ${diskWord(disk.state)}`}</span>
              {!disk.is_boot && canEdit ? <>
                <button type="button" className={DISK_CHIP} disabled={locked || disk.size_gb >= 1024} onClick={() => open({ action: 'resize', disk })}>확장</button>
                <button type="button" className={DISK_CHIP} disabled={locked} onClick={() => open({ action: 'remove', disk })}>삭제</button>
              </> : null}
            </div>
            {!disk.is_boot ? (
              <div className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                <code className="min-w-0 break-all">{disk.guest_path}</code>
                <CopyButton text={disk.guest_path} label={`디스크 ${disk.slot} 장치 경로 복사`} icon />
              </div>
            ) : null}
            {/* 다시 하라는 지시는 그 칩이 선 사람에게만 — 남의 VM 을 보는 관리자에게는 막힘만 말한다. */}
            {disk.failure_reason ? <div className="mt-1 text-[12px] text-danger-ink">{canEdit ? '디스크 작업이 실패했습니다. 다시 시도하세요.' : '디스크 작업이 실패했습니다.'}</div> : null}
            {admin ? <ExpertLine mono items={[disk.device, disk.storage, disk.volid, disk.failure_reason]} /> : null}
          </div>
        ))}
      </div>
      <Dialog open={change !== null} onOpenChange={(value) => { if (!value && !busy) setChange(null) }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          {change?.action === 'resize' ? <select aria-label="디스크 크기" className="rounded-md border bg-background px-3 py-2" value={size} onChange={(event) => setSize(Number(event.target.value))} disabled={busy}>{sizes.map((n) => <option key={n} value={n}>{sizeText(n)}</option>)}</select> : null}
          <Err error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setChange(null)} disabled={busy}>취소</Button>
            <Button type="button" onClick={save} disabled={locked}>{busy ? '처리 중' : change?.action === 'resize' ? '확장' : '삭제'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  )
}
